import {
    DEFAULT_TOOL_PROVIDER,
    toolNameKey,
} from "pages/missionTemplates/utils/templateTools";
import { normalizePenibilite } from "pages/missionTemplates/utils/templateRiskyPositions";

// Tool names of the referential may start with a space: tools and licences
// are compared trimmed and case-insensitively.
const nameKey = toolNameKey;

// Tools are listed trimmed by the Prerequisites step, while the licence
// select matches names exactly as stored: `trim` follows each step.
const toUniqueNames = (input, { trim }) => {
    if (!Array.isArray(input)) return [];
    const seen = new Set();
    return input.reduce((names, name) => {
        const key = nameKey(name);
        if (!key || seen.has(key)) return names;
        seen.add(key);
        names.push(trim ? name.trim() : name);
        return names;
    }, []);
};

const EMPTY_PREFILL = {
    position_at_risk: false,
    penibilite: {},
    tools: [],
    licences: [],
};

/**
 * What a qualification suggests for the risk and prerequisites steps. An
 * older qualification without these fields suggests nothing.
 */
export const getQualificationPrefill = (qualification) => {
    if (!qualification) return null;
    const positionAtRisk = qualification.conditions?.position_at_risk === true;
    return {
        position_at_risk: positionAtRisk,
        penibilite: positionAtRisk
            ? normalizePenibilite(qualification.conditions?.penibilite)
            : {},
        tools: toUniqueNames(qualification.tools, { trim: true }),
        licences: toUniqueNames(qualification.licences, { trim: false }),
    };
};

/**
 * Applies the new qualification's suggestions to the wizard data, like
 * `replaceProposedTasks` does for tasks: what the previous qualification
 * suggested and the company left untouched is replaced, what the company
 * added or changed is kept. `prefill` null = qualification cleared.
 *
 * `templateData.qualificationPrefill` remembers the last applied suggestions
 * so a later change can tell them apart; it is never sent to the api.
 */
export const applyQualificationPrefill = (templateData, prefill) => {
    const previous = templateData.qualificationPrefill || EMPTY_PREFILL;
    const next = prefill || EMPTY_PREFILL;

    // Penibilite only holds checked factors: a factor of the previous
    // qualification still checked is one the company did not touch.
    const companyPenibilite = normalizePenibilite(templateData.penibilite);
    Object.keys(previous.penibilite).forEach((besttField) => {
        delete companyPenibilite[besttField];
    });
    const penibilite = { ...companyPenibilite, ...next.penibilite };

    // A qualification not at risk only turns off the switch the previous
    // one turned on, and only when the company checked no factor of its own.
    let positionAtRisk = !!templateData.position_at_risk;
    if (next.position_at_risk) {
        positionAtRisk = true;
    } else if (previous.position_at_risk && positionAtRisk) {
        positionAtRisk = Object.keys(companyPenibilite).length > 0;
    }

    // A suggested tool whose provider was switched away from the default counts as the company's own choice.
    const previousToolKeys = new Set(previous.tools.map(nameKey));
    const keptTools = (
        Array.isArray(templateData.tools) ? templateData.tools : []
    ).filter(
        (tool) =>
            !(
                previousToolKeys.has(nameKey(tool?.name)) &&
                tool?.providedBy === DEFAULT_TOOL_PROVIDER
            )
    );
    const keptToolKeys = new Set(keptTools.map((tool) => nameKey(tool?.name)));
    const tools = [
        ...keptTools,
        ...next.tools
            .filter((name) => !keptToolKeys.has(nameKey(name)))
            .map((name) => ({ name, providedBy: DEFAULT_TOOL_PROVIDER })),
    ];

    const previousLicenceKeys = new Set(previous.licences.map(nameKey));
    const keptLicences = (
        Array.isArray(templateData.licences) ? templateData.licences : []
    ).filter((licence) => !previousLicenceKeys.has(nameKey(licence)));
    const keptLicenceKeys = new Set(keptLicences.map(nameKey));
    const licences = [
        ...keptLicences,
        ...next.licences.filter((name) => !keptLicenceKeys.has(nameKey(name))),
    ];

    return {
        ...templateData,
        position_at_risk: positionAtRisk,
        penibilite,
        tools,
        licences,
        qualificationPrefill: prefill || null,
    };
};
